/**
 * Browse by Category — a global discovery page. Pick a category (bar, grocery, laundry, salon…)
 * and see matching branches from every venue on the platform. Tapping a branch opens its hub.
 * Route: /customer/categories
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, ChevronRight } from 'lucide-react';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import Pagination from '@/components/common/Pagination/Pagination';
import styles from './BrowseCategories.module.scss';

interface Category { id: string; code: string; label: string; icon: string | null; description: string | null; count: number; }
interface CatBranch { id: string; organizerId: string; organizerName: string | null; organizerLogo: string | null; name: string; address: string | null; city: string | null; state: string | null; isActive: boolean; }

const PAGE_SIZE = 8;

/** An emoji for a category — prefer the type's own icon (if it's an emoji), else keyword-match. */
function catEmoji(cat: { code?: string; label?: string; icon?: string | null }): string {
  const icon = (cat.icon || '').trim();
  // A short non-ascii string is almost certainly an emoji already.
  if (icon && icon.length <= 4 && [...icon].some((ch) => (ch.codePointAt(0) || 0) > 127)) return icon;
  const key = `${cat.code || ''} ${cat.label || ''}`.toLowerCase();
  const has = (...ws: string[]) => ws.some((w) => key.includes(w));
  if (has('bar', 'pub', 'brew')) return '🍺';
  if (has('grocery', 'mart', 'supermarket')) return '🛒';
  if (has('laundry', 'dry clean')) return '🧺';
  if (has('salon', 'saloon', 'barber', 'hair')) return '💇';
  if (has('spa', 'massage')) return '💆';
  if (has('restaurant', 'dining', 'diner', 'food')) return '🍽️';
  if (has('cafe', 'coffee', 'bistro')) return '☕';
  if (has('hotel', 'resort', 'inn')) return '🏨';
  if (has('billiard', 'pool', 'snooker', 'cue')) return '🎱';
  if (has('lounge')) return '🛋️';
  if (has('gym', 'fitness')) return '🏋️';
  if (has('pharmacy', 'medical', 'clinic')) return '💊';
  if (has('bakery', 'cake')) return '🥐';
  if (has('shop', 'store', 'retail')) return '🛍️';
  return '🏢';
}

const BrowseCategories: React.FC = () => {
  const navigate = useNavigate();

  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Category | null>(null);
  const [branches, setBranches] = useState<CatBranch[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'auto' }); }, []);

  useEffect(() => {
    let alive = true;
    setLoadingCats(true);
    apiService.getBranchCategories()
      .then((res: any) => { if (alive) setCategories(Array.isArray(res?.categories) ? res.categories : []); })
      .catch((e) => { if (alive) setError(e instanceof Error ? e.message : 'Could not load categories'); })
      .finally(() => { if (alive) setLoadingCats(false); });
    return () => { alive = false; };
  }, []);

  const openCategory = useCallback(async (cat: Category) => {
    setSelected(cat); setPage(1); setBranches([]); setLoadingBranches(true); setError(null);
    window.scrollTo({ top: 0, behavior: 'auto' });
    try {
      const res: any = await apiService.getBranchesByCategory(cat.code);
      setBranches(Array.isArray(res?.branches) ? res.branches : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load branches');
    } finally { setLoadingBranches(false); }
  }, []);

  const pagedBranches = branches.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <DashboardHeader />
      <div className={styles.page}>
        <div className={styles.content}>
          {selected ? (
            <>
              <button className={styles.back} onClick={() => { setSelected(null); setError(null); }}>
                <ArrowLeft size={16} /> All categories
              </button>
              <h1 className={styles.title}><span className={styles.titleEmoji}>{catEmoji(selected)}</span> {selected.label}</h1>
              <p className={styles.subtitle}>
                {loadingBranches ? 'Loading venues…' : `${branches.length} ${branches.length === 1 ? 'venue' : 'venues'}`}
              </p>

              {error && <div className={styles.error}>⚠️ {error}</div>}

              {loadingBranches ? (
                <div className={styles.branchList}>
                  {[0, 1, 2].map((i) => <div key={i} className={styles.skeletonRow} />)}
                </div>
              ) : branches.length === 0 ? (
                <div className={styles.empty}>No venues in this category yet.</div>
              ) : (
                <>
                  <div className={styles.branchList}>
                    {pagedBranches.map((b) => (
                      <button
                        key={b.id}
                        className={styles.branchCard}
                        onClick={() => navigate(`/customer/organizers/${b.organizerId}/branches/${b.id}`)}
                      >
                        {b.organizerLogo
                          ? <img src={b.organizerLogo} alt={b.organizerName || b.name} className={styles.branchLogo} />
                          : <span className={styles.branchEmoji}>{catEmoji(selected)}</span>}
                        <span className={styles.branchInfo}>
                          <span className={styles.branchName}>{b.organizerName || b.name}</span>
                          <span className={styles.branchAddr}>
                            <MapPin size={12} /> {b.organizerName
                              ? [b.name, b.city].filter(Boolean).join(' · ')
                              : ([b.address, b.city, b.state].filter(Boolean).join(', ') || 'Address not available')}
                          </span>
                        </span>
                        <ChevronRight size={18} className={styles.chevron} />
                      </button>
                    ))}
                  </div>
                  <Pagination page={page} total={branches.length} pageSize={PAGE_SIZE} onChange={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
                </>
              )}
            </>
          ) : (
            <>
              <button className={styles.back} onClick={() => navigate('/customer/organizers')}>
                <ArrowLeft size={16} /> Organizers
              </button>
              <h1 className={styles.title}>Browse by category</h1>
              <p className={styles.subtitle}>Pick a category to discover venues near you</p>

              {error && <div className={styles.error}>⚠️ {error}</div>}

              {loadingCats ? (
                <div className={styles.catGrid}>
                  {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className={styles.skeletonCat} />)}
                </div>
              ) : categories.length === 0 ? (
                <div className={styles.empty}>No categories available yet.</div>
              ) : (
                <div className={styles.catGrid}>
                  {categories.map((c) => (
                    <button key={c.id} className={styles.catCard} onClick={() => void openCategory(c)}>
                      <span className={styles.catEmoji}>{catEmoji(c)}</span>
                      <span className={styles.catLabel}>{c.label}</span>
                      <span className={styles.catCount}>{c.count} {c.count === 1 ? 'venue' : 'venues'}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BrowseCategories;
